import { type ObstacleDefinition } from "@common/definitions/obstacles";
import { type Hitbox, HitboxType } from "@common/utils/hitbox";
import { casterHeight } from "./shadowLogic";

/*
    Lights blocked by walls (0.98.7, the owner, 1 November 2026: "Lights blocked by walls"): the parts without Pixi
    (tests/src/update0987.test.ts). A light's reach is drawn as a fan: from the light, a ray at every angle that
    matters stops at the first wall it meets (or at the light's reach), and the fan's points in their order round the
    light make the shape the light fills, so a doorway throws a beam across the street and a crate stands in its own
    dark. The walls are the hitboxes of what stands on the ground: each as its edges (a circle as a few of them).
*/

/** a light's cone (a flashlight's): its middle angle and half its width (radians); none: all round */
export interface Cone {
    readonly angle: number
    readonly half: number
}

/** segments as a flat list: x1, y1, x2, y2, ... */
export type Segments = Float32Array;

/** how many rays go round a light besides the walls' corners (the fan's roundness where nothing blocks it) */
export const BASE_RAYS = 36;
/** the corners' rays are cast a hair either side of each corner too: the wall's shadow has a sharp edge */
const EPSILON = 0.0006;
/** at most this many segments are looked at for one light (the nearest), so a crowded street stays cheap */
export const MOST_SEGMENTS = 140;
/** a circle's edges */
const CIRCLE_SIDES = 10;

/** the hitbox's edges appended to `out` (world units) */
export function segmentsOf(hitbox: Hitbox, out: number[]): void {
    switch (hitbox.type) {
        case HitboxType.Rect: {
            const { min, max } = hitbox;
            out.push(min.x, min.y, max.x, min.y, max.x, min.y, max.x, max.y, max.x, max.y, min.x, max.y, min.x, max.y, min.x, min.y);
            break;
        }
        case HitboxType.Circle: {
            const { position, radius } = hitbox;
            for (let i = 0; i < CIRCLE_SIDES; i++) {
                const a0 = (i / CIRCLE_SIDES) * Math.PI * 2, a1 = ((i + 1) / CIRCLE_SIDES) * Math.PI * 2;
                out.push(position.x + Math.cos(a0) * radius, position.y + Math.sin(a0) * radius, position.x + Math.cos(a1) * radius, position.y + Math.sin(a1) * radius);
            }
            break;
        }
        case HitboxType.Polygon: {
            const points = hitbox.points;
            for (let i = 0; i < points.length; i++) {
                const a = points[i], b = points[(i + 1) % points.length];
                out.push(a.x, a.y, b.x, b.y);
            }
            break;
        }
        case HitboxType.Group:
            for (const part of hitbox.hitboxes) segmentsOf(part, out);
            break;
    }
}

/** the angle `a` turned into (-π, π] */
const wrap = (a: number): number => {
    let r = a;
    while (r > Math.PI) r -= Math.PI * 2;
    while (r <= -Math.PI) r += Math.PI * 2;
    return r;
};

/**
 * where a ray from (px, py) along (dx, dy) (a unit vector) first meets the segments, nearer than `most`: that
 * distance, or `most`
 */
function nearest(px: number, py: number, dx: number, dy: number, segments: Segments, count: number, most: number): number {
    let best = most;
    for (let i = 0; i < count; i++) {
        const x1 = segments[i * 4], y1 = segments[i * 4 + 1], x2 = segments[i * 4 + 2], y2 = segments[i * 4 + 3];
        const ex = x2 - x1, ey = y2 - y1;
        const den = dx * ey - dy * ex;
        if (Math.abs(den) < 1e-9) continue;
        const fx = x1 - px, fy = y1 - py;
        const t = (fx * ey - fy * ex) / den;
        if (t <= 0 || t >= best) continue;
        const u = (fx * dy - fy * dx) / den;
        if (u < 0 || u > 1) continue;
        best = t;
    }
    return best;
}

/**
 * the fan of a light at (px, py) reaching `radius`, blocked by `segments` (world units; only those within reach
 * count), all round or within `cone`: the fan's points (x, y, ...) in their order round the light, from the cone's
 * one edge to its other (all round: the first point follows the last). Drawn as triangles from the light to each
 * pair of points in turn
 */
export function visibilityFan(px: number, py: number, radius: number, segments: Segments, cone?: Cone): Float32Array {
    return fanAmong(px, py, radius, nearSegments(px, py, radius, segments), cone);
}

/**
 * the segments near enough to block a light at (px, py) reaching `radius` (a segment wholly further than the reach
 * can't), the nearest `MOST_SEGMENTS` of them when there are more, in their order: all a light's fan is made of
 * (0.98.8: so a fan is kept while these stay the same, `LightFans.fan`, whatever moves further off)
 */
export function nearSegments(px: number, py: number, radius: number, segments: Segments): Float32Array {
    const near: number[] = [];
    const reach2 = radius * radius;
    const total = segments.length >> 2;
    const picked: Array<{ d: number, i: number }> = [];
    for (let i = 0; i < total; i++) {
        const x1 = segments[i * 4], y1 = segments[i * 4 + 1], x2 = segments[i * 4 + 2], y2 = segments[i * 4 + 3];
        // the segment's nearest point to the light
        const ex = x2 - x1, ey = y2 - y1;
        const len2 = ex * ex + ey * ey;
        let t = len2 > 0 ? ((px - x1) * ex + (py - y1) * ey) / len2 : 0;
        t = Math.min(1, Math.max(0, t));
        const cx = x1 + ex * t - px, cy = y1 + ey * t - py;
        const d2 = cx * cx + cy * cy;
        if (d2 < reach2) picked.push({ d: d2, i });
    }
    if (picked.length > MOST_SEGMENTS) {
        picked.sort((a, b) => a.d - b.d);
        picked.length = MOST_SEGMENTS;
    }
    for (const { i } of picked) near.push(segments[i * 4], segments[i * 4 + 1], segments[i * 4 + 2], segments[i * 4 + 3]);
    return new Float32Array(near);
}

/** a light's fan (as `visibilityFan`) among the segments near it (`nearSegments`) */
export function fanAmong(px: number, py: number, radius: number, nearSegments: Float32Array, cone?: Cone): Float32Array {
    const count = nearSegments.length >> 2;

    const angles: number[] = [];
    const centre = cone ? cone.angle : 0;
    const half = cone ? cone.half : Math.PI;
    const within = (a: number): boolean => !cone || Math.abs(wrap(a - centre)) <= half + 1e-9;
    // the base rays round the light (within the cone: from its one edge to its other)
    if (cone) {
        const steps = Math.max(2, Math.ceil(BASE_RAYS * half / Math.PI));
        for (let i = 0; i <= steps; i++) angles.push(centre - half + (2 * half * i) / steps);
    } else {
        for (let i = 0; i < BASE_RAYS; i++) angles.push(-Math.PI + (Math.PI * 2 * i) / BASE_RAYS);
    }
    // a ray at every corner, and a hair either side of it
    for (let i = 0; i < count; i++) {
        for (const k of [0, 2]) {
            const x = nearSegments[i * 4 + k], y = nearSegments[i * 4 + k + 1];
            const a = Math.atan2(y - py, x - px);
            for (const b of [a - EPSILON, a, a + EPSILON]) if (within(b)) angles.push(b);
        }
    }
    // in their order round the light (relative to the cone's middle, so a cone across -π reads in one sweep)
    const keyed = angles.map(a => ({ a, key: cone ? wrap(a - centre) : wrap(a) }));
    keyed.sort((p, q) => p.key - q.key);
    const points: number[] = [];
    let lastKey = Number.NaN;
    for (const { a, key } of keyed) {
        if (Math.abs(key - lastKey) < 1e-7) continue;
        lastKey = key;
        const dx = Math.cos(a), dy = Math.sin(a);
        const t = nearest(px, py, dx, dy, nearSegments, count, radius);
        points.push(px + dx * t, py + dy * t);
    }
    return new Float32Array(points);
}

/** how far a ray from (px, py) along (dx, dy) (a unit vector) goes before the segments stop it, at most `most` */
export function rayReach(px: number, py: number, dx: number, dy: number, segments: Segments, most: number): number {
    return nearest(px, py, dx, dy, segments, segments.length >> 2, most);
}

/**
 * whether an obstacle standing in a light's way stops it (0.98.7, its audit): a wall, a shut door, or anything
 * `least` units tall or taller that a player can't walk through and that isn't glass (a window, a pane, a glass
 * balustrade, a glass door let the light through; a cobweb, a bush, a rug don't stop it); `doorShut` for a door
 */
export function blocksLight(definition: ObstacleDefinition, least: number, doorShut: boolean): boolean {
    if (definition.noCollisions || definition.isWindow || definition.material === "glass") return false;
    if (definition.isDoor) return doorShut;
    return definition.isWall === true || definition.wall !== undefined || casterHeight(definition) >= least;
}

/** where a fan's points read its light's picture: (x, y) in the world's pixels to u = u0 + ux x + uy y, v = v0 + vx x + vy y */
export interface UvMap {
    u0: number
    ux: number
    uy: number
    v0: number
    vx: number
    vy: number
}

/** a round light's picture over `r` round (cx, cy), into `out` */
export function roundUv(cx: number, cy: number, r: number, out: UvMap): UvMap {
    const k = 1 / (2 * r);
    out.ux = k; out.uy = 0; out.u0 = 0.5 - cx * k;
    out.vx = 0; out.vy = k; out.v0 = 0.5 - cy * k;
    return out;
}

/** a beam's picture from (cx, cy) along a turn (its cosine and sine), `length` long and `width` wide, into `out` */
export function beamUv(cx: number, cy: number, cos: number, sin: number, length: number, width: number, out: UvMap): UvMap {
    out.ux = cos / length; out.uy = sin / length; out.u0 = -(cos * cx + sin * cy) / length;
    out.vx = -sin / width; out.vy = cos / width; out.v0 = 0.5 + (sin * cx - cos * cy) / width;
    return out;
}

/** the relief's picture is this share of the screen's pixels each way */
export const RELIEF_SCALE = 0.5;
/** a light's height over the ground (game units): how steeply it strikes the shapes beside it */
export const LIGHT_HEIGHT = Object.freeze({ beam: 1.2, lamp: 4.5, fire: 0.7, flash: 1.6 });
/** at most this many fans are shaped a frame (the rest of the lights are drawn round, as before) */
export const MOST_FANS = 40;
/** a light that hasn't moved this far (game units) keeps its fan, when nothing round it changed */
export const STILL = 0.02;

export const RELIEF_VERTEX = /* glsl */`
in vec2 aPosition;
in vec2 aUV;
out vec2 vWorld;
out vec2 vScreen;

uniform mat3 uProjectionMatrix;
uniform mat3 uWorldTransformMatrix;
uniform mat3 uTransformMatrix;
uniform vec3 uCamera;
uniform vec2 uScreen;

void main(void) {
    mat3 mvp = uProjectionMatrix * uWorldTransformMatrix * uTransformMatrix;
    gl_Position = vec4((mvp * vec3(aPosition, 1.0)).xy, 0.0, 1.0);
    vWorld = aPosition;
    // where this point of the world is on screen (0..1): the camera's offset and scale
    vScreen = (uCamera.xy + aPosition * uCamera.z) / uScreen;
}
`;

/**
 * a light's relief: how it strikes the shapes under its fan (the normals' picture), red where it lights a slope
 * toward it more than flat ground, green where a slope faces away from it
 */
export const RELIEF_FRAGMENT = /* glsl */`precision highp float;
in vec2 vWorld;
in vec2 vScreen;
out vec4 finalColor;

uniform sampler2D uNormal;
uniform vec2 uLight;
uniform float uRadius;
uniform float uHeight;
uniform float uStrength;
uniform vec3 uCone;

void main(void) {
    vec3 n = texture(uNormal, vScreen).rgb * 2.0 - 1.0;
    vec2 d = uLight - vWorld;
    float r = length(d);
    float fall = 1.0 - smoothstep(0.25, 1.0, r / uRadius);
    if (uCone.z > 0.0) {
        float off = abs(atan(-d.y, -d.x) - uCone.x);
        off = min(off, 6.2831853 - off);
        fall *= 1.0 - smoothstep(uCone.z * 0.5, uCone.z, off);
    }
    vec3 l = normalize(vec3(d, uHeight));
    float relief = (dot(n, l) - l.z) * uStrength * fall;
    finalColor = vec4(max(relief, 0.0), max(-relief, 0.0), 0.0, 1.0);
}
`;

/**
 * the triangles of a fan as indices into its points (0 the light itself, 1.. the fan's points), closed round when
 * `closed`; `capacity` triangles at least (the rest empty, all three corners the light's: a mesh keeps its buffers'
 * size from frame to frame as its fan changes, so they're written again, not made again)
 */
export function fanIndices(pointCount: number, closed: boolean, capacity = 0): Uint32Array {
    const triangles = closed ? pointCount : Math.max(0, pointCount - 1);
    const out = new Uint32Array(Math.max(triangles, capacity) * 3);
    for (let i = 0; i < triangles; i++) {
        out[i * 3] = 0;
        out[i * 3 + 1] = 1 + i;
        out[i * 3 + 2] = 1 + ((i + 1) % pointCount);
    }
    return out;
}

/** a fan's buffers are made this many triangles big, the next power of two of its own and 32 at least */
export function fanCapacity(pointCount: number): number {
    let capacity = 32;
    while (capacity < pointCount) capacity *= 2;
    return capacity;
}

/** whether a point is inside the fan (for the tests: the light sees it) */
export function fanContains(px: number, py: number, fan: Float32Array, x: number, y: number, closed: boolean): boolean {
    const n = fan.length >> 1;
    const triangles = closed ? n : n - 1;
    for (let i = 0; i < triangles; i++) {
        const ax = fan[i * 2], ay = fan[i * 2 + 1];
        const j = (i + 1) % n;
        const bx = fan[j * 2], by = fan[j * 2 + 1];
        const d1 = (x - px) * (ay - py) - (y - py) * (ax - px);
        const d2 = (x - ax) * (by - ay) - (y - ay) * (bx - ax);
        const d3 = (x - bx) * (py - by) - (y - by) * (px - bx);
        const neg = d1 < 0 || d2 < 0 || d3 < 0;
        const pos = d1 > 0 || d2 > 0 || d3 > 0;
        if (!(neg && pos)) return true;
    }
    return false;
}
